// @ts-nocheck
/**
 * Search Blacklist or DND — POST API_SEARCH_BLACKLIST_DND with {mobile:Number},
 * result status branches (BLACKLIST / DND / BLACKLIST-DND / not found).
 * Remove-from-Blacklist path fetches full list then finds mobile → id, then
 * deletes by id (2-step). Empty 204 tolerated via try/catch.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { deleteApiCallWithHeader } from '@/service/deleteApiCall';
import {
  API_SEARCH_BLACKLIST_DND,
  API_DND_ADD,
  API_BLACKLIST_LIST,
  API_BLACKLIST_DELETE,
} from '@/service/url';
import { getUser } from '@/utilities/setting';
import { ROUTES, NUMBER_OPS_DND_ROLES } from '@/utilities/constant';
import { Button } from '@/components/ui/button';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import {
  Search as SearchIcon,
  Ban,
  PhoneOff,
  ShieldCheck,
  Loader2,
  ShieldAlert,
} from 'lucide-react';

const SearchDndBlist = () => {
  const [mobile, setMobile] = useState('');
  const [searchResult, setSearchResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState('');

  const handleMobileChange = (e: any) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
    setMobile(val);
  };

  const doSearch = async (mobileVal: any) => {
    const data: any = await postApiCall(API_SEARCH_BLACKLIST_DND, {
      mobile: Number(mobileVal),
    });
    setSearchResult(data);
  };

  const handleSearch = async (e: any) => {
    e.preventDefault();
    if (!mobile || mobile.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }
    setLoading(true);
    setSearchResult(null);
    try {
      await doSearch(mobile);
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveBlacklist = async () => {
    setActionLoading('blacklist');
    try {
      const user = getUser();
      let list: any = [];
      try {
        list = await getApiCall(API_BLACKLIST_LIST(user?.id), {});
      } catch (_) {
        // 204 or error — list stays empty
      }
      const entry = Array.isArray(list)
        ? list.find((item: any) => String(item.mobile) === String(searchResult.mobile))
        : null;
      if (!entry) {
        toast.error('Blacklist entry not found');
        return;
      }
      await deleteApiCallWithHeader(API_BLACKLIST_DELETE(entry.id));
      toast.success('Number removed from Blacklist');
      await doSearch(mobile);
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Failed to remove from Blacklist');
    } finally {
      setActionLoading('');
    }
  };

  const handleRemoveDnd = async () => {
    setActionLoading('dnd');
    try {
      await postApiCall(API_DND_ADD, {
        mobile: Number(searchResult.mobile),
        operation: 'remove',
      });
      toast.success('Number removed from DND');
      await doSearch(mobile);
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Failed to remove from DND');
    } finally {
      setActionLoading('');
    }
  };

  const status = searchResult?.status || '';
  const notFound = status === 'Number not found in Blacklist or DND';
  const onBlacklist = status === 'BLACKLIST' || status === 'BLACKLIST-DND';
  const onDnd = status === 'DND' || status === 'BLACKLIST-DND';
  const canDnd = NUMBER_OPS_DND_ROLES.includes(getUser()?.role);

  return (
    <ScrollPage>
      <PageHeader
        title="Search Blacklist / DND"
        description="Look up whether a mobile number is on the Blacklist, DND, or both."
        actions={
          <Button asChild variant="outline">
            <Link to={`/${ROUTES.NUMBER_OPERATIONS.VIEW_BLACKLIST_DND}`}>
              <ShieldAlert className="h-4 w-4" />
              View lists
            </Link>
          </Button>
        }
      />

      <div
        className="rounded-2xl border border-border/60 bg-card p-5"
        style={{
          boxShadow:
            '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
        }}
      >
        <form onSubmit={handleSearch}>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px] space-y-1.5">
              <label className="text-xs font-semibold text-foreground">
                Mobile number
              </label>
              <input
                type="text"
                inputMode="numeric"
                placeholder="10-digit mobile number"
                value={mobile}
                onChange={handleMobileChange}
                maxLength={10}
                className="input-embossed h-10 w-full rounded-md px-3 font-mono text-sm tracking-wider"
              />
            </div>
            <Button type="submit" disabled={loading} className="h-10 gap-1.5">
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <SearchIcon className="h-4 w-4" />
              )}
              {loading ? 'Searching…' : 'Search'}
            </Button>
          </div>
        </form>

        {searchResult && (
          <div className="mt-5 rounded-xl border border-border/60 bg-muted/30 p-4">
            <div className="mb-3 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Result
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[180px]">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Mobile
                </div>
                <div className="font-mono text-lg font-semibold tracking-wider text-foreground">
                  {searchResult.mobile}
                </div>
              </div>

              <div className="flex flex-wrap gap-1.5">
                {notFound ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Not on any list
                  </span>
                ) : (
                  <>
                    {onBlacklist && (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-100 px-3 py-1 text-xs font-semibold text-rose-700 ring-1 ring-inset ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300">
                        <Ban className="h-3.5 w-3.5" />
                        Blacklist
                      </span>
                    )}
                    {onDnd && (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700 ring-1 ring-inset ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300">
                        <PhoneOff className="h-3.5 w-3.5" />
                        DND
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>

            {!notFound && (
              <div className="mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-border/50 pt-3">
                {onBlacklist && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!!actionLoading}
                    onClick={handleRemoveBlacklist}
                    className="gap-1.5"
                  >
                    {actionLoading === 'blacklist' ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Ban className="h-3.5 w-3.5" />
                    )}
                    {actionLoading === 'blacklist'
                      ? 'Removing…'
                      : 'Remove from Blacklist'}
                  </Button>
                )}
                {onDnd && canDnd && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!!actionLoading}
                    onClick={handleRemoveDnd}
                    className="gap-1.5"
                  >
                    {actionLoading === 'dnd' ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <PhoneOff className="h-3.5 w-3.5" />
                    )}
                    {actionLoading === 'dnd' ? 'Removing…' : 'Remove from DND'}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </ScrollPage>
  );
};

export default SearchDndBlist;
